import type { SmartCreationBrowserDraft } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

/** What a viewer without edit permission sees instead of the wizard. */
export function ReadonlyPlanSummary({ plan }: { plan: SmartCreationBrowserDraft }) {
  const { t } = useTranslation("smartCreation");
  return (
    <StepLayout title={t("readonly.title")} description={t("readonly.description")}>
      <div className="grid grid-cols-3 gap-3">
        <SummaryMetric
          label={t("readonly.status")}
          value={t(`readonly.statusLabels.${plan.status}`)}
        />
        <SummaryMetric label={t("readonly.step")} value={t(`steps.${plan.draft.currentStep}`)} />
        <SummaryMetric label={t("readonly.products")} value={String(plan.draft.products.length)} />
      </div>
    </StepLayout>
  );
}

function StepLayout({
  title,
  description,
  trailing,
  children,
}: {
  title: string;
  description: string;
  trailing?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-end justify-between gap-6">
        <div className="flex flex-col gap-1.5">
          <h2 className="text-2xl font-bold leading-[30px] tracking-[-0.025em] text-ink">
            {title}
          </h2>
          <p className="max-w-[820px] text-[13px] leading-5 text-muted-foreground">{description}</p>
        </div>
        {trailing ? (
          <span className="shrink-0 font-mono text-xs text-muted-foreground">{trailing}</span>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function SummaryMetric({
  label,
  value,
  className,
  valueClassName,
}: {
  label: string;
  value: string;
  className?: string;
  valueClassName?: string;
}) {
  return (
    <div className={cn("rounded-md border border-line bg-surface px-4 py-3", className)}>
      <p className="text-[11px] uppercase tracking-[0.04em] text-muted-foreground">{label}</p>
      <p
        className={cn("mt-1 font-mono text-xl font-semibold tabular-nums text-ink", valueClassName)}
      >
        {value}
      </p>
    </div>
  );
}
